import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Receipt, DownloadSimple, Eye, CheckCircle, FileText, Buildings
} from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { generateInvoicePDF } from '../utils/pdfExport';

export default function InvoicesPage() {
  const { user, apiCall } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [viewInvoice, setViewInvoice] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      loadTenants();
    } else {
      loadMyInvoices();
    }
  }, []);

  async function loadTenants() {
    try {
      const res = await apiCall('get', '/tenants');
      setTenants(res.data || []);
      if (res.data?.length > 0) {
        setSelectedTenantId(res.data[0].id);
        loadTenantInvoices(res.data[0].id);
      } else { setLoading(false); }
    } catch { setLoading(false); }
  }

  async function loadTenantInvoices(tid) {
    setLoading(true);
    try {
      const res = await apiCall('get', `/billing/invoices/${tid}`);
      setInvoices(res.data || []);
    } catch { setInvoices([]); }
    finally { setLoading(false); }
  }

  async function loadMyInvoices() {
    try {
      const res = await apiCall('get', '/billing/my-invoices');
      setInvoices(res.data || []);
    } catch { setInvoices([]); }
    finally { setLoading(false); }
  }

  function handleTenantChange(tid) {
    setSelectedTenantId(tid);
    loadTenantInvoices(tid);
  }

  function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '-'; }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Receipt size={22} className="text-primary" weight="duotone" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Invoices</h1>
            <p className="text-sm text-muted-foreground">{invoices.length} paid invoice{invoices.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Tenant selector for admin */}
      {isAdmin && tenants.length > 0 && (
        <div className="flex items-center gap-3">
          <Buildings size={16} className="text-muted-foreground" />
          <select value={selectedTenantId} onChange={e => handleTenantChange(e.target.value)} className="h-9 text-sm rounded-md border border-input bg-background px-3 min-w-[200px]" data-testid="invoice-tenant-select">
            {tenants.map(t => <option key={t.id} value={t.id}>{t.organization || t.name}</option>)}
          </select>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <Card className="shadow-card rounded-lg border-0">
          <CardContent className="p-12 text-center text-muted-foreground">
            <Receipt size={48} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No invoices yet</p>
            <p className="text-sm mt-1">Invoices will appear here after successful payments.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-card rounded-lg border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Invoice</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Date</TableHead>
                {isAdmin && <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Tenant</TableHead>}
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Plan</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Amount</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Status</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv, i) => (
                <TableRow key={inv.id || i} className="hover:bg-secondary/30" data-testid={`invoice-row-${i}`}>
                  <TableCell className="text-sm font-mono font-medium text-primary">{inv.invoice_number}</TableCell>
                  <TableCell className="text-sm">{fmtDate(inv.paid_at || inv.created_at)}</TableCell>
                  {isAdmin && <TableCell className="text-sm">{inv.tenant_name}</TableCell>}
                  <TableCell><Badge variant="outline" className="capitalize text-xs">{inv.plan_name || inv.plan_id}</Badge></TableCell>
                  <TableCell className="text-sm font-semibold">${(inv.amount || 0).toFixed(2)}</TableCell>
                  <TableCell><Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs"><CheckCircle size={11} className="mr-1" weight="fill" /> Paid</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setViewInvoice(inv)} className="h-8 w-8 p-0" title="View" data-testid={`view-invoice-${i}`}><Eye size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => generateInvoicePDF(inv)} className="h-8 w-8 p-0" title="Download PDF" data-testid={`download-invoice-${i}`}><DownloadSimple size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Invoice Preview Dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={o => !o && setViewInvoice(null)}>
        <DialogContent className="max-w-lg" data-testid="invoice-preview">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText size={20} className="text-primary" /> Invoice Preview</DialogTitle></DialogHeader>
          {viewInvoice && (
            <div className="space-y-5 py-2">
              {/* Invoice header */}
              <div className="bg-primary/5 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Invoice</p>
                    <p className="text-lg font-bold text-primary font-mono">{viewInvoice.invoice_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">RoofMate</p>
                    <p className="text-xs text-muted-foreground">Measurement Platform</p>
                  </div>
                </div>
              </div>

              {/* Bill to + Details */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Billed To</p>
                  <p className="text-sm font-semibold">{viewInvoice.tenant_name}</p>
                  <p className="text-xs text-muted-foreground">{viewInvoice.tenant_email}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Details</p>
                  <div className="text-xs space-y-0.5">
                    <p>Date: <span className="font-medium">{fmtDate(viewInvoice.paid_at || viewInvoice.created_at)}</span></p>
                    <p>Status: <span className="text-emerald-600 font-medium">Paid</span></p>
                    <p>By: <span className="font-medium">{viewInvoice.created_by || viewInvoice.metadata?.admin_email || '-'}</span></p>
                  </div>
                </div>
              </div>

              {/* Line items */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-secondary/50 px-4 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider grid grid-cols-3">
                  <span>Description</span><span>Plan</span><span className="text-right">Amount</span>
                </div>
                <div className="px-4 py-3 grid grid-cols-3 text-sm">
                  <span className="font-medium">{viewInvoice.plan_name || viewInvoice.plan_id} Subscription</span>
                  <span><Badge variant="outline" className="capitalize text-xs">{viewInvoice.plan_id}</Badge></span>
                  <span className="text-right font-semibold">${(viewInvoice.amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-end">
                <div className="bg-primary/5 rounded-lg px-6 py-3 text-right">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold text-primary">${(viewInvoice.amount || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Plan includes */}
              {viewInvoice.plan_details?.max_projects && (
                <div className="text-xs text-muted-foreground">
                  Plan includes: {viewInvoice.plan_details.max_projects >= 999999 ? 'Unlimited' : viewInvoice.plan_details.max_projects} Projects, {viewInvoice.plan_details.max_users >= 999999 ? 'Unlimited' : viewInvoice.plan_details.max_users} Users
                </div>
              )}

              <Button onClick={() => generateInvoicePDF(viewInvoice)} className="w-full rounded-md" data-testid="preview-download-btn">
                <DownloadSimple size={16} className="mr-2" /> Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
